import { BasicProgressBar } from "@playlive/overlay-core/progress-bars/basic";
import { useEffect, useState } from "preact/hooks";
import { useMetaTags } from "react-metatags-hook";
import LoadingSpinner from "@/components/shared/loading-spinner.jsx";
import SelfService from "@/components/shared/self-service.jsx";
import useTiltifyCampaign from "@/hooks/useTiltifyCampaign.js";
import useTiltifyConfig from "@/hooks/useTiltifyConfig";
import { buildCustomizedURL } from "@/util/common";
import { CHARITY, isTestMode } from "@/util/const";
import FriendsgivingCustomizeAlerts from "./friendsgiving-customize-alerts.jsx";
import FriendsgivingCustomizePolls from "./friendsgiving-customize-polls.jsx";

const fullYear = new Date().getUTCFullYear();
const pageName = `Friendsgiving ${fullYear} Tools`;
const pageSubtitle = "Self-Service";
const pageTitle = `${pageName} ${pageSubtitle} | ${isTestMode ? "" : "St. Jude "}Play Live Tools`;
const friendsgivingCampaignID = import.meta.env.VITE_FRIENDSGIVING_CAMPAIGN_ID;

export default function FriendsgivingSelfServicePage() {
  useMetaTags(
    {
      title: pageTitle,
      description: `Self-service page for Friendsgiving ${fullYear}`,
      openGraph: {
        type: "article",
        description: `Self-service page for Friendsgiving ${fullYear}`,
      },
    },
    [],
  );

  const tiltifyConfig = useTiltifyConfig();

  const [currentPollID, setCurrentPollID] = useState(null);
  const [alertsMuted, setAlertsMuted] = useState(
    localStorage.getItem("friendsgiving-alerts-muted") === "true",
  );

  const { campaign, campaignLoading } = useTiltifyCampaign({
    campaignID: friendsgivingCampaignID,
  });

  useEffect(() => {
    if (alertsMuted) {
      localStorage.setItem("friendsgiving-alerts-muted", JSON.stringify(alertsMuted));
    } else {
      localStorage.removeItem("friendsgiving-alerts-muted");
    }
  }, [alertsMuted]);

  const getOverlayURL = (overlayBase, config, suffix = "") => {
    return buildCustomizedURL({
      config: {
        ...tiltifyConfig,
        charityType: CHARITY.TILTIFY,
        tiltifyUserSlug: "st-jude",
        tiltifyCampaignSlug: `friendsgiving-${fullYear}`,
        ...config,
      },

      urlBase: overlayBase,
      urlSuffix: suffix,
    });
  };

  const friendsgivingOverlays = [
    {
      name: "Max Progress Bar",
      id: "max",
      width: "1920px",
      height: "130px",
      url: getOverlayURL("progress-bars/max", {
        showRewards: "yes",
        showDonors: "yes",
        showScroller: "yes",
        showChallenges: "yes",
        scrollerPosition: "bottom",
        bgColor: "default-red",
        textColor: "light-aqua",
      }),
    },
    {
      name: "Polls",
      id: "polls",
      url: getOverlayURL(
        "overlays/polls",
        {
          pollsBackgroundColor: "multi",
          pollsType: "multiple",
          pollsShowDollars: "yes",
          pollsShowPercentage: "yes",
        },
        currentPollID ?? "",
      ),
      disableCopy: currentPollID === null,
      customize: (
        <FriendsgivingCustomizePolls pollID={currentPollID} onPollSelected={setCurrentPollID} />
      ),
    },
    {
      name: "Donor List",
      id: "donorlist",
      width: "500px",
      height: "set to size",
      url: getOverlayURL("overlays/donorlist", {
        displayIncentives: "yes",
        showDonors: "5",
        alignment: "left",
        orientation: "vertical",
        bgColor: "default-red",
      }),
    },
    {
      name: "Rewards",
      id: "rewards",
      width: "set to size",
      height: "set to size",
      url: getOverlayURL("overlays/rewards", {
        theme: "classic",
        highlightFeaturedRewards: "yes",
        showQuantityClaimed: "yes",
        rotationTimer: "0",
        showAllRewards: "yes",
        bgColor: "default-red",
      }),
    },
    {
      name: "Alerts",
      id: "alerts",
      width: "800px",
      height: "600px",
      url: getOverlayURL(
        "overlays/alerts",
        alertsMuted
          ? {
              sound: "no",
              minDonation: "0",
            }
          : {
              sound: "yes",
              volume: "50",
              minDonation: "0",
            },
      ),
      demoModeURL: getOverlayURL(
        "overlays/alerts",
        alertsMuted
          ? {
              sound: "no",
              minDonation: "0",
              demoMode: "yes",
            }
          : {
              sound: "yes",
              volume: "50",
              minDonation: "0",
              demoMode: "yes",
            },
      ),
      customize: (
        <FriendsgivingCustomizeAlerts alertsMuted={alertsMuted} setAlertsMuted={setAlertsMuted} />
      ),
    },
    {
      name: "Donation Train",
      id: "donationTrain",
      width: "800px",
      height: "600px",
      url: getOverlayURL("overlays/donation-train", {
        donationTrainTheme: "regular",
        donationTrainPosition: "top",
        donationTrainMaxVisible: "3",
        showActivationMessage: "yes",
      }),
      demoModeURL: getOverlayURL("overlays/donation-train", {
        donationTrainTheme: "regular",
        donationTrainPosition: "top",
        donationTrainMaxVisible: "3",
        showActivationMessage: "yes",
        demoMode: "yes",
      }),
    },
  ];

  return (
    <SelfService
      pageName={pageName}
      pageSubtitle={pageSubtitle}
      pageTitle={pageTitle}
      overlays={friendsgivingOverlays}
    >
      <div className="relative h-[55px] rounded-sm overflow-hidden">
        <LoadingSpinner show={campaignLoading} />

        {!campaignLoading && campaign && (
          <BasicProgressBar
            className="w-full max-w-full!"
            goalAmount={parseFloat(campaign.goal.value)}
            currentAmount={parseFloat(campaign.amount_raised.value)}
            originalGoalAmount={
              campaign.original_goal ? parseFloat(campaign.original_goal.value) : null
            }
          />
        )}
      </div>
    </SelfService>
  );
}
